import { Link } from '@tanstack/react-router';
import { ArrowLeft, LoaderCircle } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Button, Input, Label } from '@shigo-ui/ui';

import { dashboardApiErrorMessage } from '../../../lib/apiErrorMessages';
import { readShigoAppConfig } from '../../../lib/config';
import { dashboardT } from '../../../lib/messages';
import { confirmPasswordReset } from '../api';
import { AuthPanel, AuthStep } from '../components/AuthLayout';
import { AuthPasswordInput } from '../components/AuthPasswordInput';

type ResetPasswordPageProps = {
  token: string | undefined;
};

export function ResetPasswordPage({ token }: ResetPasswordPageProps) {
  const [key, setKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const normalizedToken = token?.trim() ?? '';
  const canSubmit = Boolean(normalizedToken && key.trim() && newPassword);

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    setError('');
    setSuccess(false);
    setIsSubmitting(true);

    try {
      await confirmPasswordReset(
        { token: normalizedToken, key: key.trim(), newPassword },
        readShigoAppConfig(),
      );
      setSuccess(true);
    } catch (requestError) {
      setError(dashboardApiErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthPanel
      description={dashboardT('auth.resetPassword.description')}
      step={AuthStep.ResetPassword}
      title={dashboardT('auth.resetPassword.title')}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        {!normalizedToken ? (
          <p
            className="text-destructive rounded-lg border border-current/20 bg-current/5 px-3 py-2.5 text-sm"
            role="alert"
          >
            {dashboardT('auth.resetPassword.invalidToken')}
          </p>
        ) : null}
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs" htmlFor="reset-password-key">
            {dashboardT('auth.resetPassword.key')}
          </Label>
          <Input
            autoComplete="one-time-code"
            className="h-11 rounded-lg bg-transparent text-[15px]"
            id="reset-password-key"
            name="key"
            onChange={(event) => {
              setKey(event.currentTarget.value);
            }}
            placeholder={dashboardT('auth.resetPassword.keyPlaceholder')}
            required
            value={key}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-muted-foreground text-xs" htmlFor="reset-password-new-password">
            {dashboardT('auth.resetPassword.newPassword')}
          </Label>
          <AuthPasswordInput
            autoComplete="new-password"
            id="reset-password-new-password"
            name="newPassword"
            onChange={(event) => {
              setNewPassword(event.currentTarget.value);
            }}
            placeholder={dashboardT('auth.resetPassword.newPasswordPlaceholder')}
            required
            value={newPassword}
          />
        </div>
        {success ? (
          <p
            className="rounded-lg border border-emerald-600/20 bg-emerald-600/8 px-3 py-2.5 text-sm font-medium text-emerald-700 dark:text-emerald-400"
            role="status"
          >
            {dashboardT('auth.resetPassword.success')}
          </p>
        ) : null}
        {error ? (
          <p
            className="text-destructive rounded-lg border border-current/20 bg-current/5 px-3 py-2.5 text-sm"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <Button className="mt-2 h-11 w-full" disabled={!canSubmit || isSubmitting} type="submit">
          {isSubmitting ? (
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : null}
          {isSubmitting
            ? dashboardT('auth.resetPassword.submitting')
            : dashboardT('auth.resetPassword.submit')}
        </Button>
        <Button asChild className="w-full" variant="ghost">
          <Link to="/login">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {dashboardT('auth.resetPassword.backToLogin')}
          </Link>
        </Button>
      </form>
    </AuthPanel>
  );
}
